import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import { authUserMetaAtom, hasPermissionAtom } from '@/store/authAtoms';
import { ADMIN_ACCESS } from '@/constants/adminAccess';
import { authService } from '@/services/authService';
import {
  USER_MESSAGES_POLL_MS,
  USER_MESSAGES_QUERY_ROOT,
  USER_MESSAGES_UNREAD_QUERY_KEY,
  userMessageService,
} from '@/services/userMessageService';
import { ToastContainer } from '@/components/common/Toast';
import { AdminSessionTimeoutDialog } from '@/components/common/AdminSessionTimeoutDialog';
import { useAdminScreenSession } from '@/hooks/useAdminScreenSession';
import { cn } from '@/lib/utils';

const SIDEBAR_EXPANDED_W = 256;
const SIDEBAR_COLLAPSED_W = 64;

type NavItem = {
  to: string;
  label: string;
  icon: React.ReactNode;
  badge: number;
  accessString?: string;
  /** Re-clicking this item (including while already active) signals the page to return home. */
  resetOnClick?: boolean;
};

/* ── Icons ── */
function UsersIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
    </svg>
  );
}

function ChartBarIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
    </svg>
  );
}

function SearchIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </svg>
  );
}

function MessageIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
    </svg>
  );
}

function DatabaseIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 5.625c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
    </svg>
  );
}

function CogIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}

function BookIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
    </svg>
  );
}

function ParagraphConfigIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
    </svg>
  );
}

function SiteGroupIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" />
    </svg>
  );
}

function SystemHealthIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h3l2.25-4.5L12 17.25l2.25-9 2.25 4.5h3" />
    </svg>
  );
}

function ErrorLogIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
    </svg>
  );
}

function WorkflowFailureIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  );
}

function HistoryIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m6-2a10 10 0 1 1-2.93-7.07" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 3H9m0 0L6 6m3-3L6 0" />
    </svg>
  );
}


function SystemConfigIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}

function PaletteIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c-4.97 0-9 3.694-9 8.25 0 4.556 4.03 8.25 9 8.25.83 0 1.5-.67 1.5-1.5 0-.39-.15-.744-.4-1.01a1.49 1.49 0 0 1-.4-1.01c0-.83.67-1.5 1.5-1.5h1.77c2.5 0 4.53-2.03 4.53-4.53C20.5 6.13 16.69 3 12 3Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 12a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm2.5-3.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm5 0a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm2.5 3.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" />
    </svg>
  );
}

function LayersIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="m12 3 9 5-9 5-9-5 9-5Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="m3.5 12 8.5 4.7 8.5-4.7M3.5 16l8.5 4.7 8.5-4.7" />
    </svg>
  );
}

function EnvelopeIcon({ size = 18 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15M12 9l3 3m0 0-3 3m3-3H2.25" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
    </svg>
  );
}

function ChevronDownIcon({ size = 12 }: Readonly<{ size?: number }>) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true" focusable="false">
      <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

/* ── Logo / Wordmark ── */
function DHWordmark({ compact = false }: Readonly<{ compact?: boolean }>) {
  return (
    <div className={cn('flex items-center shrink-0', compact ? 'justify-center' : 'gap-2.5')}>
      <div
        className="flex items-center justify-center shrink-0 rounded-xl"
        style={{
          width: 36,
          height: 36,
          background: 'linear-gradient(135deg, #a10693 0%, #61045a 100%)',
          boxShadow: '0 4px 12px rgba(161,6,147,0.35)',
        }}
      >
        <svg viewBox="0 0 24 24" fill="white" width={18} height={18} aria-hidden="true" focusable="false">
          <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
        </svg>
      </div>
      {!compact && (
        <div className="hidden sm:block overflow-hidden">
          <div className="text-sm font-semibold text-gray-900 leading-tight whitespace-nowrap">Admin Portal</div>
          <div className="text-[10px] text-gray-500 font-medium tracking-wider uppercase leading-none mt-0.5 whitespace-nowrap">
            Admin · DeepHealth
          </div>
        </div>
      )}
    </div>
  );
}

/* ── User Avatar ── */
function UserAvatar({ name, size = 32 }: Readonly<{ name?: string; size?: number }>) {
  const initial = name ? (name[0]?.toUpperCase() ?? 'A') : 'A';
  return (
    <div
      className="rounded-full flex items-center justify-center shrink-0 font-semibold text-white"
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.35,
        background: 'linear-gradient(135deg, #a10693, #61045a)',
        boxShadow: '0 2px 8px rgba(161,6,147,0.3)',
      }}
    >
      {initial}
    </div>
  );
}

/* ── Mobile Drawer ── */
function MobileDrawer({
  groups,
  collapsedGroups,
  onToggleGroup,
  userId,
  onClose,
  onLogout,
  t,
  triggerRef,
}: Readonly<{
  groups: { key: string; label: string; items: NavItem[] }[];
  collapsedGroups: Record<string, boolean>;
  onToggleGroup: (key: string) => void;
  userId: string;
  onClose: () => void;
  onLogout: () => void;
  t: (k: string) => string;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}>) {
  const drawerRef = useRef<HTMLDialogElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavClick = (tab: NavItem, e: React.MouseEvent) => {
    onClose();
    if (!tab.resetOnClick) return;
    e.preventDefault();
    navigate(tab.to, {
      replace: location.pathname === tab.to,
      state: { navHomeReset: Date.now() },
    });
  };

  useEffect(() => {
    const firstFocusable = drawerRef.current?.querySelector<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();
    return () => { triggerRef.current?.focus(); };
  }, [triggerRef]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const focusables = Array.from(
      drawerRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? []
    );
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables.at(-1);
    if (e.shiftKey) {
      if (first && document.activeElement === first) { e.preventDefault(); last?.focus(); }
    } else if (last && document.activeElement === last) {
      e.preventDefault(); first?.focus();
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[150] bg-black/40 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <dialog
        ref={drawerRef}
        aria-label={t('nav.drawerMenu')}
        open
        className="fixed inset-y-0 start-0 z-[160] w-[280px] flex flex-col bg-white m-0 p-0 border-0 max-h-none h-dvh"
        style={{ boxShadow: '4px 0 32px rgba(0,0,0,0.12)' }}
      >
        <div className="flex flex-col h-full" onKeyDown={handleKeyDown}>
          <div className="shrink-0 flex items-center justify-between px-4 h-14 border-b border-gray-100">
            <DHWordmark />
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label={t('nav.closeMenu')}
            >
              <CloseIcon />
            </button>
          </div>

          <div className="shrink-0 px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-3 px-2 py-2 rounded-xl bg-gray-50">
              <UserAvatar name={userId} size={36} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 truncate">{userId}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t('account.adminAccount')}</p>
              </div>
            </div>
          </div>

          <nav aria-label={t('nav.drawerMenu')} className="flex-1 min-h-0 px-3 py-3 overflow-y-auto">
            {groups.map((group, groupIdx) => (
              <div key={group.key}>
                <button
                  onClick={() => onToggleGroup(group.key)}
                  aria-expanded={!collapsedGroups[group.key]}
                  className={cn(
                    'w-full flex items-center justify-between pl-1 pr-1.5 py-1 mb-0.5',
                    'text-[10px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600 transition-colors',
                    groupIdx === 0 ? 'mt-2' : 'mt-5'
                  )}
                >
                  <span>{group.label}</span>
                  <span
                    className="transition-transform duration-200"
                    style={{ transform: collapsedGroups[group.key] ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                  >
                    <ChevronDownIcon size={11} />
                  </span>
                </button>
                {!collapsedGroups[group.key] && (
                  <div className="ml-2 pl-[3px] border-l border-gray-100 space-y-[2px]">
                    {group.items.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                onClick={(e) => handleNavClick(tab, e)}
                className={({ isActive }) => cn(
                          'flex items-center gap-2.5 pl-3 pr-2 py-[7px] rounded-lg text-[13px] font-medium transition-all',
                  isActive
                    ? 'bg-[#a10693]/10 text-[#a10693]'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                )}
              >
                {tab.badge > 0 ? (
                  <Badge badgeContent={tab.badge} color="error" max={99}>
                    {tab.icon}
                  </Badge>
                ) : tab.icon}
                <span className="flex-1">{tab.label}</span>
              </NavLink>
            ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="shrink-0 border-t border-gray-100 px-3 py-3 pb-safe">
            <button
              onClick={() => { onClose(); onLogout(); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-500 hover:bg-red-50 hover:text-red-600 transition-all"
            >
              <LogoutIcon />
              {t('nav.logout')}
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
}

/* ── Main Layout ── */
export default function AppLayout() {
  const [user, setUser] = useAtom(authUserMetaAtom);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [routeAnnouncement, setRouteAnnouncement] = useState('');
  const prevPathRef = useRef(location.pathname);

  const [collapsed, setCollapsed] = useState<boolean>(() =>
    localStorage.getItem('admin-sidebar-collapsed') === 'true'
  );

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem('admin-sidebar-collapsed', String(next));
      return next;
    });
  }, []);

  const handleLogout = useCallback(() => {
    void authService.logout().catch(() => undefined);
    setUser(null);
  }, [setUser]);
  const screenSession = useAdminScreenSession();

  const canUserMsgs = useAtomValue(hasPermissionAtom(ADMIN_ACCESS.userMsgs));

  const { data: unreadCountData } = useQuery({
    queryKey: USER_MESSAGES_UNREAD_QUERY_KEY,
    queryFn: () => userMessageService.getUnreadCount(),
    refetchInterval: USER_MESSAGES_POLL_MS,
    staleTime: 0,
    enabled: !!user && canUserMsgs,
  });
  const unreadCount = unreadCountData?.count ?? 0;
  const previousUnreadCount = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (unreadCountData?.count === undefined) return;
    const previous = previousUnreadCount.current;
    previousUnreadCount.current = unreadCountData.count;
    if (previous === undefined || previous === unreadCountData.count) return;

    void queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === USER_MESSAGES_QUERY_ROOT
        && query.queryKey[1] !== 'unread-count'
        && query.queryKey[1] !== 'detail',
    });
  }, [queryClient, unreadCountData?.count]);

  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem('admin-nav-groups') ?? '{}'); } catch { return {}; }
  });

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem('admin-nav-groups', JSON.stringify(next));
      return next;
    });
  }, []);

  const navGroups = useMemo(() => {
    const permissions = user?.permissions ?? [];
    const can = (accessString?: string) =>
      !accessString || permissions.includes(`${accessString}.Full`);

    return [
      {
        key: 'administration',
        label: t('nav.groupAdministration'),
        items: [
          { to: '/dashboard', label: t('nav.dashboard'), icon: <ChartBarIcon />, badge: 0 },
          { to: '/patient-management', label: t('nav.patientManagement'), icon: <UsersIcon />, badge: 0, accessString: ADMIN_ACCESS.userMgt },
          { to: '/audit-search', label: t('nav.auditSearch'), icon: <SearchIcon />, badge: 0, accessString: ADMIN_ACCESS.audit },
          { to: '/system-messages', label: t('nav.systemMessages'), icon: <MessageIcon />, badge: 0, accessString: ADMIN_ACCESS.sysMsgs },
          { to: '/user-messages', label: t('nav.userMessages'), icon: <EnvelopeIcon />, badge: unreadCount, accessString: ADMIN_ACCESS.userMsgs },
          { to: '/cache-management', label: t('nav.cacheManagement'), icon: <DatabaseIcon />, badge: 0, accessString: ADMIN_ACCESS.cacheInfo },
          { to: '/training-materials', label: t('nav.trainingMaterials'), icon: <BookIcon />, badge: 0, accessString: ADMIN_ACCESS.trainingMaterial },
        ].filter((item) => can(item.accessString)) as NavItem[],
      },
      {
        key: 'configuration',
        label: t('nav.groupConfiguration'),
        items: [
          { to: '/portal-config', label: t('nav.portalAppConfig'), icon: <CogIcon />, badge: 0, accessString: ADMIN_ACCESS.configAccess },
          { to: '/theme-editor', label: t('nav.themeEditor'), icon: <PaletteIcon />, badge: 0, accessString: ADMIN_ACCESS.configAccess },
          { to: '/theme-base-editor', label: t('nav.themeBaseEditor'), icon: <LayersIcon />, badge: 0, accessString: ADMIN_ACCESS.configAccess },
          { to: '/paragraph-config', label: t('nav.paragraphConfig'), icon: <ParagraphConfigIcon />, badge: 0, accessString: ADMIN_ACCESS.configAccess },
          { to: '/system-config', label: t('nav.systemConfig'), icon: <SystemConfigIcon />, badge: 0, accessString: ADMIN_ACCESS.configAccess },
          { to: '/site-group-editor', label: t('nav.siteGroupEditor'), icon: <SiteGroupIcon />, badge: 0, accessString: ADMIN_ACCESS.configAccess },
          { to: '/mobile-clinic-day-url', label: 'Mobile Scheduling', icon: <SiteGroupIcon />, badge: 0, accessString: ADMIN_ACCESS.configAccess, resetOnClick: true },
        ].filter((item) => can(item.accessString)) as NavItem[],
      },
      {
        key: 'diagnostics',
        label: t('nav.groupDiagnostics'),
        items: [
          { to: '/system-health', label: t('nav.systemHealth'), icon: <SystemHealthIcon />, badge: 0, accessString: ADMIN_ACCESS.systemHealth },
          { to: '/error-logs', label: t('nav.errorLogs'), icon: <ErrorLogIcon />, badge: 0, accessString: ADMIN_ACCESS.errorLogs },
          { to: '/workflow-failures', label: t('nav.workflowFailures'), icon: <WorkflowFailureIcon />, badge: 0, accessString: ADMIN_ACCESS.workflowFailures },
          { to: '/lookup-change-history', label: t('nav.lookupChangeHistory'), icon: <HistoryIcon />, badge: 0, accessString: ADMIN_ACCESS.lookupHistory },
        ].filter((item) => can(item.accessString)) as NavItem[],
      },
    ].filter((group) => group.items.length > 0);
  }, [t, unreadCount, user?.permissions]);

  const allTabs = navGroups.flatMap((g) => g.items);

  const handleDesktopNavClick = useCallback(
    (tab: NavItem, e: React.MouseEvent) => {
      if (!tab.resetOnClick) return;
      e.preventDefault();
      navigate(tab.to, {
        replace: location.pathname === tab.to,
        state: { navHomeReset: Date.now() },
      });
    },
    [location.pathname, navigate],
  );

  const [sidebarDragWidth, setSidebarDragWidth] = useState<number | null>(null);
  const sidebarDragging = useRef(false);

  const onSidebarDragStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    sidebarDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: PointerEvent) => {
      if (!sidebarDragging.current) return;
      const newW = Math.max(SIDEBAR_COLLAPSED_W, Math.min(ev.clientX, 400));
      setSidebarDragWidth(newW);
    };
    const onUp = () => {
      sidebarDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setSidebarDragWidth((w) => {
        if (w !== null && w <= SIDEBAR_COLLAPSED_W + 20) {
          if (!collapsed) toggleCollapsed();
          return null;
        }
        if (w !== null && collapsed) toggleCollapsed();
        return w;
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [collapsed, toggleCollapsed]);

  const sidebarWidth = sidebarDragWidth ?? (collapsed ? SIDEBAR_COLLAPSED_W : SIDEBAR_EXPANDED_W);

  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-w', `${sidebarWidth}px`);
  }, [sidebarWidth]);

  useEffect(() => {
    if (location.pathname === prevPathRef.current) return;
    prevPathRef.current = location.pathname;
    const tab = allTabs.find(tab => location.pathname.startsWith(tab.to));
    const pageName = tab?.label ?? 'Admin Portal';
    document.title = `${pageName} — Admin Portal`;
    setRouteAnnouncement(`Navigated to ${pageName}`);
  });

  const userId = user?.userId ?? 'Admin';

  return (
    <div className="flex min-h-screen w-full overflow-x-hidden" style={{ background: 'var(--dh-bg-default)' }}>

      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:start-2 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-white focus:text-gray-900 focus:rounded-lg focus:shadow-lg focus:text-sm focus:font-medium focus:border focus:border-gray-200"
      >
        {t('common.skipToMain')}
      </a>

      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {routeAnnouncement}
      </div>

      {/* ── Desktop Sidebar ── */}
      <aside
        aria-label={t('nav.sidebarLabel')}
        aria-hidden={mobileOpen ? 'true' : undefined}
        className="hidden lg:flex flex-col fixed start-0 top-0 h-screen z-50 overflow-hidden"
        style={{
          width: sidebarWidth,
          transition: sidebarDragWidth ? 'none' : 'width 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          background: '#fff',
          borderRight: '1px solid #f0f0f0',
          boxShadow: '1px 0 0 0 #f3f4f6',
        }}
      >
        <div className={cn('flex items-center py-[18px] shrink-0', collapsed ? 'justify-center px-3.5' : 'px-5')}>
          <DHWordmark compact={collapsed} />
        </div>

        <div
          className="mx-3 mb-3 h-px rounded-full shrink-0"
          aria-hidden="true"
          style={{ background: 'linear-gradient(90deg, #a10693 0%, transparent 100%)', opacity: 0.25 }}
        />

        <nav aria-label={t('nav.primary')} className="flex-1 px-2 py-1 overflow-y-auto overflow-x-hidden" style={{ scrollbarWidth: 'thin', scrollbarColor: '#e5e7eb transparent' }}>
          {navGroups.map((group, groupIdx) => (
            <div key={group.key}>
              {!collapsed ? (
                <button
                  onClick={() => toggleGroup(group.key)}
                  aria-expanded={!collapsedGroups[group.key]}
                  className={cn(
                    'w-full flex items-center justify-between pl-1 pr-1.5 py-1 mb-0.5',
                    'text-[10px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600 transition-colors',
                    groupIdx === 0 ? 'mt-2' : 'mt-5'
                  )}
                >
                  <span>{group.label}</span>
                  <span
                    className="transition-transform duration-200"
                    style={{ transform: collapsedGroups[group.key] ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                  >
                    <ChevronDownIcon size={11} />
                  </span>
                </button>
              ) : (
                groupIdx > 0 && <div className="mx-2 my-2 h-px bg-gray-100" aria-hidden="true" />
              )}
              {(!collapsedGroups[group.key] || collapsed) && (
                <div className={collapsed ? 'space-y-[2px]' : 'ml-2 pl-[3px] border-l border-gray-100 space-y-[2px]'}>
                  {group.items.map((tab) => (
                    <Tooltip
                      key={tab.to}
                      title={collapsed ? tab.label : ''}
                      placement="right"
                      arrow
                    >
                      {/* div wrapper is required — NavLink doesn't forward refs, so Tooltip would otherwise inject an anonymous span that breaks the flex row */}
                      <div>
                        <NavLink
                          to={tab.to}
                          onClick={(e) => handleDesktopNavClick(tab, e)}
                          className={({ isActive }) =>
                            cn(
                              'flex items-center rounded-lg text-[13px] font-medium transition-all relative',
                              collapsed ? 'justify-center px-0 py-2.5 w-full' : 'gap-2.5 pl-3 pr-2 py-[7px]',
                              isActive
                                ? 'bg-[#a10693]/10 text-[#a10693]'
                                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                            )
                          }
                        >
                          <span className={cn('shrink-0 flex items-center justify-center overflow-visible', collapsed ? 'w-full' : '')}>
                            {tab.badge > 0 ? (
                              <Badge badgeContent={tab.badge} color="error" max={99}>
                                {tab.icon}
                              </Badge>
                            ) : tab.icon}
                          </span>
                          {!collapsed && (
                            <span className="flex-1 truncate">{tab.label}</span>
                          )}
                        </NavLink>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className={cn('py-3 border-t border-gray-100 space-y-1 mt-2 shrink-0', collapsed ? 'px-2' : 'px-3')}>
          <Tooltip title={collapsed ? userId : ''} placement="right" arrow>
            <div
              className={cn(
                'flex items-center rounded-xl',
                collapsed ? 'justify-center py-2' : 'gap-3 px-3 py-2.5'
              )}
            >
              <UserAvatar name={userId} size={30} />
              {!collapsed && (
                <div className="flex-1 min-w-0" aria-hidden="true">
                  <p className="text-sm font-semibold text-gray-900 truncate leading-tight">{userId}</p>
                  <p className="text-[11px] text-gray-600 leading-tight">{t('account.adminAccount')}</p>
                </div>
              )}
            </div>
          </Tooltip>

          <Tooltip title={collapsed ? t('nav.logout') : ''} placement="right" arrow>
            <button
              onClick={handleLogout}
              aria-label={t('nav.logout')}
              className={cn(
                'w-full flex items-center rounded-xl text-sm text-gray-500 hover:bg-red-50 hover:text-red-500 transition-all',
                collapsed ? 'justify-center py-2' : 'gap-3 px-3 py-2.5'
              )}
            >
              <LogoutIcon />
              {!collapsed && <span>{t('nav.logout')}</span>}
            </button>
          </Tooltip>

          <div className={cn('pt-1', collapsed ? '' : 'border-t border-gray-100 mt-1')}>
            <Tooltip title={collapsed ? t('nav.expandSidebar') : ''} placement="right" arrow>
              <button
                onClick={toggleCollapsed}
                aria-label={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
                aria-expanded={!collapsed}
                className={cn(
                  'w-full flex items-center rounded-xl text-xs text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-all',
                  collapsed ? 'justify-center py-2' : 'gap-2 px-3 py-2'
                )}
              >
                {collapsed ? <ChevronRightIcon /> : (
                  <>
                    <ChevronLeftIcon />
                    <span>{t('nav.collapseSidebar')}</span>
                  </>
                )}
              </button>
            </Tooltip>
          </div>
        </div>

        <div
          className="absolute end-0 top-0 w-1 h-full cursor-col-resize group z-10"
          onPointerDown={onSidebarDragStart}
        >
          <div className="absolute end-0 top-1/2 -translate-y-1/2 w-0.5 h-8 rounded-full bg-transparent group-hover:bg-purple-400 group-active:bg-purple-600 transition-colors" />
        </div>
      </aside>

      {/* ── Content Area ── */}
      <div
        className="sidebar-content-offset flex-1 flex flex-col min-h-screen lg:h-screen w-full min-w-0 overflow-x-hidden"
        aria-hidden={mobileOpen ? 'true' : undefined}
      >
        {/* Mobile header */}
        <header
          className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4"
          style={{ height: 56, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', borderBottom: '1px solid #f0f0f0' }}
        >
          <DHWordmark />
          <button
            ref={menuButtonRef}
            onClick={() => setMobileOpen(true)}
            className="p-2.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label={t('nav.openMenu')}
            aria-expanded={mobileOpen}
            aria-haspopup="dialog"
          >
            <MenuIcon />
          </button>
        </header>

        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 lg:min-h-0 w-full min-w-0 px-3 sm:px-5 lg:px-6 py-3 sm:py-4 lg:py-6 lg:overflow-y-auto overflow-x-hidden"
        >
          <Outlet />
        </main>

        <ToastContainer />

        <footer className="hidden lg:block" style={{ borderTop: '1px solid #f0f0f0', background: '#fff' }}>
          <div
            className="px-4 lg:px-6 py-3 flex items-center justify-between"
            style={{ fontSize: '12px', color: '#6b7280' }}
          >
            <span>© {new Date().getFullYear()} DeepHealth Admin Portal — All rights reserved</span>
          </div>
        </footer>
      </div>

      {mobileOpen && (
        <MobileDrawer
          groups={navGroups}
          collapsedGroups={collapsedGroups}
          onToggleGroup={toggleGroup}
          userId={userId}
          onClose={() => setMobileOpen(false)}
          onLogout={handleLogout}
          t={t}
          triggerRef={menuButtonRef}
        />
      )}
      <AdminSessionTimeoutDialog
        phase={screenSession.phase}
        countdown={screenSession.countdown}
        onExtend={screenSession.extend}
        onLogout={screenSession.logout}
      />
    </div>
  );
}
